import React, { useState, useEffect, useCallback, useRef } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FiClock, FiCalendar, FiExternalLink, FiChevronRight, FiCheckCircle } from "react-icons/fi";
import Context from "../../Context";

/**
 * Format timestamp into human-readable Thai time with relative indicator
 */
export const formatScheduleTime = (timestamp) => {
  if (!timestamp) return "-";
  const target = new Date(Number(timestamp));
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const isToday = target.toDateString() === now.toDateString();
  const timeStr = `${String(target.getHours()).padStart(2, "0")}:${String(target.getMinutes()).padStart(2, "0")} น.`;

  const datePrefix = isToday
    ? "วันนี้"
    : `${String(target.getDate()).padStart(2, "0")}/${String(target.getMonth() + 1).padStart(2, "0")}/${target.getFullYear() + 543}`;

  if (diffMs <= 0) {
    return `${datePrefix} ${timeStr} (ถึงเวลาแล้ว)`;
  }
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 60) {
    return `${datePrefix} ${timeStr} (อีก ${diffMin} นาที)`;
  }
  const diffHours = Math.floor(diffMin / 60);
  const remMin = diffMin % 60;
  return `${datePrefix} ${timeStr} (อีก ${diffHours} ชม. ${remMin > 0 ? `${remMin} นาที` : ""})`;
};

/**
 * Hook to get real-time pending schedules from Firestore with fallback (Zero polling)
 */
export const usePendingSchedules = () => {
  const { db, callApi } = Context();
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const callApiRef = useRef(callApi);

  useEffect(() => {
    callApiRef.current = callApi;
  }, [callApi]);

  const fetchViaApi = useCallback(async () => {
    try {
      const apiFn = callApiRef.current;
      if (!apiFn) return;
      const res = await apiFn(
        "schedules-list",
        { status: "pending" },
        { notShowLoading: true }
      );
      if (res && Array.isArray(res.data)) {
        setSchedules(res.data);
      }
    } catch (e) {
      // quiet error on background fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe = null;

    if (db) {
      try {
        const q = query(
          collection(db, "schedules"),
          where("status", "==", "pending")
        );
        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (!isMounted) return;
            const items = snapshot.docs
              .map((doc) => ({ id: doc.id, ...doc.data() }))
              .sort((a, b) => Number(a.executeAt || 0) - Number(b.executeAt || 0));
            setSchedules(items);
            setIsLoading(false);
          },
          (err) => {
            console.warn("[usePendingSchedules] onSnapshot error, falling back to API once:", err);
            if (isMounted) {
              fetchViaApi();
            }
          }
        );
      } catch (err) {
        console.warn("[usePendingSchedules] setup error:", err);
        fetchViaApi();
      }
    } else {
      fetchViaApi();
    }

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [db, fetchViaApi]);

  return { schedules, isLoading };
};

/**
 * ScheduledTasksWidget for Dashboard and Admin Center
 */
export const ScheduledTasksWidget = ({ variant = "dashboard", className = "" }) => {
  const { schedules } = usePendingSchedules();
  const navigate = useNavigate();

  // If variant is dashboard and there are no schedules, render a subtle indicator or nothing
  if (schedules.length === 0) {
    if (variant === "compact") return null;
    return null; // Keep dashboard/admin clean when no upcoming tasks
  }

  // Dashboard & Admin Center variant: Clean, orderly and easy-to-read table
  return (
    <div
      className={`w-full bg-white border border-amber-300 rounded-md shadow-xs overflow-hidden ${className}`}
    >
      {/* Header Bar */}
      <div className="p-3 bg-linear-to-r from-amber-500/10 via-orange-500/5 to-transparent border-b border-amber-200 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-2xs">
            <FiClock className="text-xs animate-pulse" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-xs text-slate-900">
                คิวงานตั้งเวลาล่วงหน้า (Scheduled Tasks)
              </span>
              <span className="px-2 py-0.2 rounded-full text-[10px] font-extrabold bg-amber-500 text-white">
                {schedules.length} งานรอทำงาน
              </span>
            </div>
            <p className="text-[11px] text-slate-500">
              รายการที่ระบบ Universal Queue จะดำเนินการปรับใช้หรือคืนค่าให้อัตโนมัติเมื่อถึงเวลา
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/prices")}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 active:scale-95 text-white text-xs font-bold transition shadow-xs cursor-pointer"
        >
          <span>จัดการราคาและคิวงาน</span>
          <FiExternalLink className="text-[11px]" />
        </button>
      </div>

      {/* Clean, Orderly Table */}
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-50 text-slate-600 font-bold border-b border-gray-200 text-[11px] sticky top-0 z-10 shadow-2xs">
            <tr>
              <th className="py-2 px-3 w-10 text-center">#</th>
              <th className="py-2 px-3 w-28">หมวดหมู่</th>
              <th className="py-2 px-3">ชื่องาน / การกระทำ</th>
              <th className="py-2 px-3 w-64">กำหนดทำงาน (กี่โมง)</th>
              <th className="py-2 px-3 w-24 text-center">สถานะ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 font-semibold text-slate-700">
            {schedules.map((job, idx) => {
              const isFee = job.category === "fee" || job.updates?.fee !== undefined;
              const isPastDue = Number(job.executeAt || 0) <= Date.now();

              return (
                <tr
                  key={job.id}
                  className="hover:bg-amber-50/50 transition-colors cursor-pointer"
                  onClick={() => navigate("/prices")}
                  title="คลิกเพื่อไปที่หน้าจัดการราคาและคิวงาน"
                >
                  <td className="py-2.5 px-3 text-center text-slate-400 font-mono text-[11px]">
                    {idx + 1}
                  </td>
                  <td className="py-2.5 px-3">
                    {isFee ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                        ค่าบริการ (Fee)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                        ราคาพิเศษ (Price)
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="font-bold text-slate-900 text-xs">
                      {job.title}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-1.5 font-bold text-amber-800 text-[11px]">
                      <FiCalendar className="text-[11px] text-amber-600 shrink-0" />
                      <span>{formatScheduleTime(job.executeAt)}</span>
                      {isPastDue && (
                        <span className="px-1.5 py-0.2 rounded text-[9px] font-black bg-rose-500 text-white animate-pulse shrink-0">
                          ถึงเวลาแล้ว
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                      รอทำงาน
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduledTasksWidget;
